import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { updateProfile } from '../lib/account-api';
import { uploadOrDataUrl } from '../lib/files-api';
import { extractApiError } from '../lib/api-client';
import { useT } from '../i18n';

const MAX_MB = 2;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp';

/** Two-letter initials from a display name, for the Avatar fallback. */
export function initialsOf(name?: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function EditProfileDialog({
  open,
  onClose,
  currentName,
  currentAvatar,
  onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  currentName?: string;
  currentAvatar?: string;
  onUpdated?: () => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [origName, setOrigName] = useState('');
  const [origAvatar, setOrigAvatar] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const n = currentName ?? '';
      const a = currentAvatar ?? null;
      setName(n);
      setAvatar(a);
      setOrigName(n);
      setOrigAvatar(a);
      setError(null);
      setBusy(false);
      setUploading(false);
    }
  }, [open, currentName, currentAvatar]);

  const close = () => {
    if (busy || uploading) return;
    onClose();
  };

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange again.
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(t('prof.tooBig', { max: MAX_MB }));
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const url = await uploadOrDataUrl(file);
      setAvatar(url);
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setUploading(false);
    }
  };

  const nameTrimmed = name.trim();
  const nameEmpty = nameTrimmed.length === 0;
  const dirty = nameTrimmed !== origName.trim() || avatar !== origAvatar;
  const canSave = !busy && !uploading && !nameEmpty && dirty;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: { full_name?: string; avatar_url?: string | null } = {};
      if (nameTrimmed !== origName.trim()) body.full_name = nameTrimmed;
      if (avatar !== origAvatar) body.avatar_url = avatar;
      await updateProfile(body);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>{t('prof.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }} alignItems="stretch">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={avatar ?? undefined} sx={{ width: 64, height: 64, fontSize: 24 }}>
              {initialsOf(name)}
            </Avatar>
            <Stack spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={pickFile}
                disabled={uploading || busy}
              >
                {uploading
                  ? t('prof.uploading')
                  : avatar
                  ? t('prof.replace')
                  : t('prof.upload')}
              </Button>
              {avatar ? (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setAvatar(null)}
                  disabled={uploading || busy}
                >
                  {t('prof.remove')}
                </Button>
              ) : null}
            </Stack>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={onFile}
              aria-label={t('prof.upload')}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('prof.photoHint', { max: MAX_MB })}
          </Typography>

          <TextField
            label={t('prof.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameEmpty && name.length > 0}
            helperText={nameEmpty && name.length > 0 ? t('prof.nameRequired') : ' '}
            inputProps={{ maxLength: 120 }}
            fullWidth
          />

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy || uploading}>
          {t('common.cancel')}
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSave}>
          {busy ? t('prof.saving') : t('prof.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
